import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { useKeycloak } from '@react-keycloak/web';
import { getStrafregisterIt, strafregisterItPdf, StrafregisterItInfo } from './ApiClient';
import SignaturePad from './SignaturePad';

/**
 * Strafregisterauszug (Italien): Das Gateway befüllt das Formblatt Nr. 3 der
 * Procura Bozen mit den USP-Stammdaten und der hier erfassten Unterschrift —
 * das Mitglied lädt das fertige PDF herunter und schickt den Antrag SELBST
 * per E-Mail ans Strafregisteramt (Entscheidung 30.08.2026: der Antrag soll
 * von der Person kommen, nicht vom Verein). Die Steuernummer ist nur ein
 * VORSCHLAG — die Berechnung kennt nur den ersten Vornamen.
 */
function StrafregisterIt({ goBack }: { goBack: () => void }) {
  const { keycloak } = useKeycloak();
  const [info, setInfo] = useState<StrafregisterItInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [steuernummer, setSteuernummer] = useState('');
  const [wohnsitz, setWohnsitz] = useState('');
  const [unterschrift, setUnterschrift] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fertig, setFertig] = useState<{ procura: string; mailtext: string; dateiname: string } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getStrafregisterIt(keycloak.token);
      setInfo(r);
      setSteuernummer(r.daten.steuernummer_vorschlag || '');
      setWohnsitz(r.daten.wohnsitzgemeinde || '');
    } catch (err) {
      setError('Daten konnten nicht geladen werden: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [keycloak.token]);

  useEffect(() => {
    load();
  }, [load]);

  const herunterladen = async () => {
    if (!steuernummer.trim()) return;
    setBusy(true);
    setFehler(null);
    try {
      const r = await strafregisterItPdf(keycloak.token, {
        steuernummer: steuernummer.trim().toUpperCase(),
        wohnsitzgemeinde: wohnsitz.trim(),
        unterschrift_png: unterschrift,
      });
      const bytes = Uint8Array.from(atob(r.pdf), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = r.dateiname;
      a.click();
      URL.revokeObjectURL(url);
      setFertig({ procura: r.procura, mailtext: r.mailtext, dateiname: r.dateiname });
    } catch (err) {
      setFehler('Erzeugen fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Container fluid='md' className='py-4 text-center'>
        <Spinner animation='border' role='status' />
        <p className='mt-3'>Lade deine Daten…</p>
      </Container>
    );
  }

  if (error || !info) {
    return (
      <Container fluid='md' className='py-4'>
        <Alert variant='danger'>{error || 'Keine Daten.'}</Alert>
        <Button variant='outline-secondary' onClick={load}>Erneut versuchen</Button>
      </Container>
    );
  }

  const d = info.daten;

  return (
    <Container fluid='md' className='py-4' style={{ maxWidth: 760 }}>
      <Button variant='link' className='px-0 mb-2' onClick={goBack}>← Zurück</Button>
      <h2 className='mb-3'>Strafregisterauszug (Italien) beantragen</h2>

      <Card className='mb-4'>
        <Card.Body>
          <Card.Title as='h5'>So funktioniert es</Card.Title>
          <ol className='mb-2' style={{ fontSize: '0.95rem' }}>
            <li>Wir füllen das amtliche Formblatt Nr. 3 der Staatsanwaltschaft Bozen mit deinen Daten aus</li>
            <li>Du prüfst die Steuernummer, unterschreibst hier (oder später händisch) und lädst das PDF herunter</li>
            <li>Du schickst das Formblatt <strong>selbst</strong> per E-Mail ans Strafregisteramt — zusammen mit einer Kopie
              deines Reisepasses oder Personalausweises (Adresse und fertigen Mailtext zeigen wir dir nach dem Download)</li>
            <li>Nach 1–3 Arbeitstagen kommt die Abholbenachrichtigung — abholen in Bozen mit zwei Stempelmarken (16,00 € + 3,92 €)</li>
            <li>Den fertigen Auszug lädst du dann im Portal hoch — für deine ZÜP darf er höchstens 6 Monate alt sein</li>
          </ol>
        </Card.Body>
      </Card>

      {!info.relevant && (
        <Alert variant='info'>
          Hinweis: Bei deinen Daten sehen wir keinen Italien-Bezug (Wohnsitz/Geburtsland). Du kannst
          trotzdem fortfahren, wenn du den Auszug brauchst.
        </Alert>
      )}

      <section className='form-section mb-4'>
        <h5 className='mb-3'>Deine Daten auf dem Formblatt</h5>
        <Table borderless size='sm' responsive>
          <tbody>
            <tr><td className='text-muted'>Name</td><td>{d.vorname} {d.famname}</td></tr>
            <tr><td className='text-muted'>Geboren</td><td>{d.gebdatum} in {d.geb_ort}</td></tr>
            <tr><td className='text-muted'>Beantragt wird</td><td>Strafregisterauszug (Art. 24 E.T.), 1 Ausfertigung</td></tr>
          </tbody>
        </Table>
        <Form.Group className='mb-3'>
          <Form.Label>Wohnsitzgemeinde</Form.Label>
          <Form.Control value={wohnsitz} onChange={(e) => setWohnsitz(e.target.value)} />
        </Form.Group>
        <Form.Group className='mb-1'>
          <Form.Label>Steuernummer (Codice fiscale)</Form.Label>
          <Form.Control
            value={steuernummer}
            onChange={(e) => setSteuernummer(e.target.value.toUpperCase())}
            maxLength={16}
            style={{ fontFamily: 'monospace', letterSpacing: 1 }}
            placeholder='16 Zeichen'
          />
          <Form.Text className='text-muted'>
            {d.steuernummer_vorschlag
              ? 'Aus deinen Daten berechnet — bitte mit deiner Gesundheitskarte abgleichen! '
                + 'Hast du mehrere Vornamen, weicht die echte Nummer meist ab.'
              : 'Bitte von deiner Gesundheitskarte abtippen.'}
          </Form.Text>
        </Form.Group>
      </section>

      <section className='form-section mb-4'>
        <h5 className='mb-2'>Unterschrift (optional)</h5>
        <p className='text-muted mb-2' style={{ fontSize: '0.9rem' }}>
          Wird auf dem Formblatt bei „Unterschrift der beantragenden Person" eingesetzt und
          nirgends gespeichert. Du kannst das PDF auch ohne Unterschrift herunterladen und
          ausgedruckt händisch unterschreiben.
        </p>
        <SignaturePad onChange={setUnterschrift} />
      </section>

      {fehler && <Alert variant='danger'>{fehler}</Alert>}

      {fertig ? (
        <Card border='success' className='mb-3'>
          <Card.Body>
            <Card.Title as='h5'>✅ {fertig.dateiname} ist heruntergeladen — so schickst du den Antrag ab</Card.Title>
            <ol style={{ fontSize: '0.95rem' }}>
              <li>Neue E-Mail an: <strong>{fertig.procura}</strong></li>
              <li>Anhängen: das Formblatt-PDF + eine Kopie deines <strong>Reisepasses oder Personalausweises</strong></li>
              <li>Als Text kannst du das hier verwenden:</li>
            </ol>
            <Form.Control as='textarea' readOnly rows={6} value={fertig.mailtext}
              onFocus={(e) => (e.target as HTMLTextAreaElement).select()} />
            <div className='d-flex gap-2 mt-2'>
              <Button size='sm' variant='outline-secondary'
                onClick={() => navigator.clipboard?.writeText(fertig.mailtext)}>
                Mailtext kopieren
              </Button>
              <Button size='sm' variant='outline-primary'
                href={`mailto:${fertig.procura}?subject=${encodeURIComponent('Ausstellung Strafregisterauszug')}&body=${encodeURIComponent(fertig.mailtext)}`}>
                E-Mail-Entwurf öffnen
              </Button>
            </div>
            <p className='text-muted mt-3 mb-0' style={{ fontSize: '0.85rem' }}>
              Sobald du den Auszug abgeholt hast: bitte über „Dokument nachreichen" hochladen —
              dann ist deine ZÜP-Akte komplett.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Button disabled={busy || steuernummer.trim().length !== 16} onClick={herunterladen}>
          {busy ? <Spinner size='sm' animation='border' /> : 'Formblatt als PDF herunterladen'}
        </Button>
      )}
    </Container>
  );
}

export default StrafregisterIt;
